import type {
  CreateVendorInput,
  UpdateVendorInput,
  ListVendorsQuery,
  RecordPaymentInput,
  ListPurchasesQuery,
  ListLedgerQuery,
  CreatePurchaseCategoryInput,
  UpdatePurchaseCategoryInput,
  CreatePaymentModeInput,
  UpdatePaymentModeInput,
  ListFinanceAuditQuery,
  CreditReimbursementInput,
  CreateReimbursementCategoryInput,
  UpdateReimbursementCategoryInput,
  ListReimbursementLedgerQuery,
  PaginationQuery,
  DataResponse,
  PaginatedResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Response types ────────────────────────────────────────────────────────────

export interface Vendor {
  id: string;
  name: string;
  type: string;
  contact_person: string | null;
  mobile: string | null;
  email: string | null;
  credit_limit: string;
  total_purchase: string;
  total_paid: string;
  outstanding: string;
  is_active: boolean;
  created_at: string;
}

export interface VendorDetail extends Vendor {
  gst_number: string | null;
  pan: string | null;
  bank_account_name: string | null;
  bank_account_number: string | null;
  ifsc: string | null;
  upi_id: string | null;
  opening_balance: string;
}

export interface PurchaseCategory {
  id: string;
  name: string;
  monthly_budget: string;
  max_purchase_limit: string;
  gst_applicable: boolean;
  is_active: boolean;
}

export interface PaymentMode {
  id: string;
  mode_name: string;
  is_active: boolean;
  display_order: number;
}

export interface PurchaseListItem {
  id: string;
  invoice_number: string;
  invoice_date: string | null;
  purchase_date: string;
  base_amount: string;
  gst_amount: string;
  total_amount: string;
  paid_amount: string;
  balance_amount: string;
  status: 'Pending' | 'Partially Paid' | 'Paid';
  description: string | null;
  invoice_url: string | null;
  vendor_id: string;
  vendor_name: string;
  category_id: string | null;
  category_name: string | null;
  created_at: string;
}

export interface PurchasePayment {
  id: string;
  payment_amount: string;
  payment_mode: string;
  transaction_id: string;
  payment_date: string;
  remark: string | null;
  paid_by_name: string | null;
  created_at: string;
}

export interface PurchaseDetail extends PurchaseListItem {
  payments: PurchasePayment[];
}

export interface PurchaseSummary {
  total_purchase_month: number;
  total_paid_month: number;
  total_outstanding: number;
  vendors_with_pending: number;
}

export interface LedgerEntry {
  id: string;
  vendor_id: string;
  vendor_name: string;
  reference_type: 'Opening' | 'Purchase' | 'Payment';
  reference_id: string | null;
  debit: string;
  credit: string;
  running_balance: string;
  description: string | null;
  created_at: string;
}

export interface AgingRow {
  vendor_id: string;
  vendor_name: string;
  d0_30: string;
  d30_60: string;
  d60plus: string;
  total: string;
}

export interface BudgetRow {
  id: string;
  name: string;
  budget: number;
  used: number;
  remaining: number;
  pct: number;
}

export interface FinanceAuditLog {
  id: string;
  module: string;
  action: string;
  metadata: Record<string, unknown> | null;
  action_by_name: string | null;
  created_at: string;
}

export type FinanceRules = Record<string, string>;

export interface ReimbursementAccount {
  id: string;
  user_id: string;
  balance: string;
  total_credited: string;
  total_debited: string;
  is_active: boolean;
  employee_name: string;
  employee_code: string | null;
}

export interface AvailableEmployee {
  id: string;
  name: string;
  employee_code: string | null;
}

export interface ReimbursementCategory {
  id: string;
  name: string;
  monthly_limit: string;
  per_claim_limit: string;
  bill_required_above: string;
  gst_applicable: boolean;
  is_active: boolean;
}

export interface ReimbursementLedgerEntry {
  id: string;
  entry_type: 'credit' | 'debit';
  amount: string;
  running_balance: string;
  expense_date: string | null;
  description: string | null;
  project_tag: string | null;
  bill_urls: string[] | null;
  remark?: string | null;
  created_at: string;
  employee_name?: string;
  category_name: string | null;
}

export interface MyReimbursement {
  account: {
    id: string;
    balance: string;
    total_credited: string;
    total_debited: string;
    is_active: boolean;
  } | null;
  month_credit?: number;
  month_debit?: number;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const financeService = {
  // Vendors
  async listVendors(query: Partial<ListVendorsQuery> = {}): Promise<PaginatedResponse<Vendor>> {
    const res = await api.get<PaginatedResponse<Vendor>>('/finance/vendors', { params: query });
    return res.data;
  },
  async getVendor(id: string): Promise<VendorDetail> {
    const res = await api.get<DataResponse<VendorDetail>>(`/finance/vendors/${id}`);
    return res.data.data;
  },
  async createVendor(input: CreateVendorInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/vendors', input);
    return res.data.data;
  },
  async updateVendor(id: string, input: UpdateVendorInput): Promise<void> {
    await api.patch(`/finance/vendors/${id}`, input);
  },
  async deleteVendor(id: string): Promise<void> {
    await api.delete(`/finance/vendors/${id}`);
  },

  // Lookups
  async listCategories(): Promise<PurchaseCategory[]> {
    const res = await api.get<DataResponse<PurchaseCategory[]>>('/finance/categories');
    return res.data.data;
  },
  async listPaymentModes(): Promise<PaymentMode[]> {
    const res = await api.get<DataResponse<PaymentMode[]>>('/finance/payment-modes');
    return res.data.data;
  },

  // Purchases
  async listPurchases(query: Partial<ListPurchasesQuery> = {}): Promise<PaginatedResponse<PurchaseListItem>> {
    const res = await api.get<PaginatedResponse<PurchaseListItem>>('/finance/purchases', { params: query });
    return res.data;
  },
  async getPurchase(id: string): Promise<PurchaseDetail> {
    const res = await api.get<DataResponse<PurchaseDetail>>(`/finance/purchases/${id}`);
    return res.data.data;
  },
  async getSummary(): Promise<PurchaseSummary> {
    const res = await api.get<DataResponse<PurchaseSummary>>('/finance/purchases/summary');
    return res.data.data;
  },
  async createPurchase(form: FormData): Promise<{ id: string; warning?: string }> {
    const res = await api.post<DataResponse<{ id: string; warning?: string }>>('/finance/purchases', form);
    return res.data.data;
  },
  async recordPayment(purchaseId: string, input: RecordPaymentInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(`/finance/purchases/${purchaseId}/payments`, input);
    return res.data.data;
  },
  /** Fetches the invoice as an object URL (auth header applied) for inline viewing. */
  async getInvoiceUrl(purchaseId: string): Promise<string> {
    const res = await api.get(`/finance/purchases/${purchaseId}/invoice`, { responseType: 'blob' });
    return URL.createObjectURL(res.data as Blob);
  },

  // Ledger
  async getLedger(query: Partial<ListLedgerQuery> = {}): Promise<PaginatedResponse<LedgerEntry>> {
    const res = await api.get<PaginatedResponse<LedgerEntry>>('/finance/ledger', { params: query });
    return res.data;
  },
  async getAging(vendorId?: string): Promise<AgingRow[]> {
    const res = await api.get<DataResponse<AgingRow[]>>('/finance/ledger/aging', {
      params: vendorId ? { vendor_id: vendorId } : {},
    });
    return res.data.data;
  },

  // Master · categories
  async createCategory(input: CreatePurchaseCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/categories', input);
    return res.data.data;
  },
  async updateCategory(id: string, input: UpdatePurchaseCategoryInput): Promise<void> {
    await api.patch(`/finance/categories/${id}`, input);
  },
  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/finance/categories/${id}`);
  },

  // Master · payment modes
  async listAllPaymentModes(): Promise<PaymentMode[]> {
    const res = await api.get<DataResponse<PaymentMode[]>>('/finance/payment-modes/all');
    return res.data.data;
  },
  async createPaymentMode(input: CreatePaymentModeInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/payment-modes', input);
    return res.data.data;
  },
  async updatePaymentMode(id: string, input: UpdatePaymentModeInput): Promise<void> {
    await api.patch(`/finance/payment-modes/${id}`, input);
  },
  async deletePaymentMode(id: string): Promise<void> {
    await api.delete(`/finance/payment-modes/${id}`);
  },

  // Master · rules
  async getRules(): Promise<FinanceRules> {
    const res = await api.get<DataResponse<FinanceRules>>('/finance/rules');
    return res.data.data;
  },
  async setRule(key: string, value: string): Promise<void> {
    await api.patch(`/finance/rules/${key}`, { value });
  },

  // Master · budget + audit
  async getBudget(): Promise<BudgetRow[]> {
    const res = await api.get<DataResponse<BudgetRow[]>>('/finance/budget');
    return res.data.data;
  },
  async listAuditLogs(query: Partial<ListFinanceAuditQuery> = {}): Promise<PaginatedResponse<FinanceAuditLog>> {
    const res = await api.get<PaginatedResponse<FinanceAuditLog>>('/finance/audit-logs', { params: query });
    return res.data;
  },

  // Reimbursements · admin
  async listReimbursementAccounts(): Promise<ReimbursementAccount[]> {
    const res = await api.get<DataResponse<ReimbursementAccount[]>>('/finance/reimbursement/accounts');
    return res.data.data;
  },
  async listAvailableEmployees(): Promise<AvailableEmployee[]> {
    const res = await api.get<DataResponse<AvailableEmployee[]>>('/finance/reimbursement/accounts/available');
    return res.data.data;
  },
  async createReimbursementAccount(user_id: string): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/reimbursement/accounts', { user_id });
    return res.data.data;
  },
  async creditReimbursement(accountId: string, input: CreditReimbursementInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(`/finance/reimbursement/accounts/${accountId}/credit`, input);
    return res.data.data;
  },
  async listAllReimbursementLedger(query: Partial<ListReimbursementLedgerQuery> = {}): Promise<PaginatedResponse<ReimbursementLedgerEntry>> {
    const res = await api.get<PaginatedResponse<ReimbursementLedgerEntry>>('/finance/reimbursement/ledger', { params: query });
    return res.data;
  },
  async getReimbursementBudget(): Promise<BudgetRow[]> {
    const res = await api.get<DataResponse<BudgetRow[]>>('/finance/reimbursement/budget');
    return res.data.data;
  },

  // Reimbursements · categories (read shared, write admin)
  async listReimbursementCategories(): Promise<ReimbursementCategory[]> {
    const res = await api.get<DataResponse<ReimbursementCategory[]>>('/finance/reimbursement/categories');
    return res.data.data;
  },
  async createReimbursementCategory(input: CreateReimbursementCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/reimbursement/categories', input);
    return res.data.data;
  },
  async updateReimbursementCategory(id: string, input: UpdateReimbursementCategoryInput): Promise<void> {
    await api.patch(`/finance/reimbursement/categories/${id}`, input);
  },
  async deleteReimbursementCategory(id: string): Promise<void> {
    await api.delete(`/finance/reimbursement/categories/${id}`);
  },

  // Reimbursements · employee self-service
  async getMyReimbursement(): Promise<MyReimbursement> {
    const res = await api.get<DataResponse<MyReimbursement>>('/finance/reimbursement/me');
    return res.data.data;
  },
  async getMyReimbursementLedger(query: Partial<PaginationQuery> = {}): Promise<PaginatedResponse<ReimbursementLedgerEntry>> {
    const res = await api.get<PaginatedResponse<ReimbursementLedgerEntry>>('/finance/reimbursement/me/ledger', { params: query });
    return res.data;
  },
  async addReimbursementExpense(form: FormData): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/finance/reimbursement/me/expense', form);
    return res.data.data;
  },
  async getBillUrl(ledgerId: string, idx: number): Promise<string> {
    const res = await api.get(`/finance/reimbursement/bills/${ledgerId}/${idx}`, { responseType: 'blob' });
    return URL.createObjectURL(res.data as Blob);
  },
};
